import { useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { api } from '@/lib/api';
import toast from 'react-hot-toast';

type Mode = 'suspend' | 'block';

const REASONS: Record<Mode, string[]> = {
  suspend: ['Policy violation', 'Suspicious activity', 'Payment or chargeback issue', 'Repeated complaints', 'Other'],
  block: ['Fraud', 'Counterfeit goods', 'Severe abuse or harassment', 'Illegal item listed', 'Repeated serious violations', 'Other'],
};

const COPY: Record<Mode, { title: string; points: string[]; confirm: string; confirmClass: string }> = {
  suspend: {
    title: 'Are you sure you want to suspend this account?',
    points: [
      'The user will be logged out immediately and won’t be able to access the account.',
      'The account will remain suspended for 30 days.',
      'The user can submit an appeal right away, and you can review it at any time.',
    ],
    confirm: 'Suspend Account',
    confirmClass: 'bg-bad text-cream hover:opacity-90',
  },
  block: {
    title: 'Are you sure you want to block this account?',
    points: [
      'This is intended for serious violations, not routine enforcement.',
      'The user will be logged out immediately and won’t be able to access the account.',
      'The user can submit an appeal right away, and you can review it at any time.',
      'If the appeal is approved, the account is restored 7 days after that approval.',
    ],
    confirm: 'Block Account',
    confirmClass: 'bg-ink text-cream hover:opacity-90',
  },
};

function AccountActionModal({ mode, userId, userName, onClose }: { mode: Mode; userId: string; userName?: string; onClose: () => void }) {
  const qc = useQueryClient();
  const copy = COPY[mode];
  const [reason, setReason] = useState(REASONS[mode][0]);
  const [details, setDetails] = useState('');
  const isOther = reason === 'Other';
  const finalReason = isOther ? details.trim() : reason;
  const canSubmit = finalReason.length >= 3;

  const act = useMutation({
    mutationFn: async () => (await api.post(`/admin/users/${userId}/${mode}`, { reason: finalReason })).data,
    onSuccess: () => {
      toast.success(mode === 'suspend' ? 'Account suspended.' : 'Account blocked.');
      qc.invalidateQueries({ queryKey: ['admin', 'users'] });
      qc.invalidateQueries({ queryKey: ['admin', 'user', userId] });
      onClose();
    },
    onError: (e: any) => toast.error(e?.response?.data?.message ?? 'Action failed.'),
  });

  return (
    <div className="fixed inset-0 z-[60] grid place-items-center p-4 bg-black/60 backdrop-blur-sm" onClick={onClose}>
      <div className="card p-6 max-w-md w-full" onClick={(e) => e.stopPropagation()}>
        <div className="stamp text-clay-dark">{mode === 'suspend' ? 'Suspend' : 'Block'} account{userName ? ` - ${userName}` : ''}</div>
        <h2 className="display text-2xl mt-1">{copy.title}</h2>
        <ul className="mt-3 space-y-1.5">
          {copy.points.map((p) => (
            <li key={p} className="text-sm text-mute flex gap-2">
              <span className="text-bad shrink-0">•</span><span>{p}</span>
            </li>
          ))}
        </ul>

        <label className="label mt-4">Reason</label>
        <select className="input" value={reason} onChange={(e) => setReason(e.target.value)}>
          {REASONS[mode].map((r) => <option key={r}>{r}</option>)}
        </select>
        {isOther && (
          <>
            <label className="label mt-3">Details</label>
            <textarea
              className="input min-h-[90px]"
              value={details}
              maxLength={500}
              onChange={(e) => setDetails(e.target.value)}
              placeholder="Describe the reason for this action…"
            />
          </>
        )}

        <div className="flex gap-2 mt-5">
          <button onClick={onClose} className="btn btn-outline flex-1">Cancel</button>
          <button
            onClick={() => act.mutate()}
            disabled={!canSubmit || act.isPending}
            className={`btn flex-1 ${copy.confirmClass}`}
          >
            {act.isPending ? 'Working…' : copy.confirm}
          </button>
        </div>
      </div>
    </div>
  );
}

export function SuspendModal(props: { userId: string; userName?: string; onClose: () => void }) {
  return <AccountActionModal mode="suspend" {...props} />;
}

export function BlockModal(props: { userId: string; userName?: string; onClose: () => void }) {
  return <AccountActionModal mode="block" {...props} />;
}
